import React, { useState, useEffect, useContext, useCallback } from "react";
import { getApiUrl } from "../config/api";
import { InternalLayout } from "../Components/InternalLayout";
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  CardMedia,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Switch,
  FormControlLabel,
  useTheme,
  useMediaQuery,
  Grid,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  YouTube as YouTubeIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ArrowBack,
} from "@mui/icons-material";
import { AuthContext } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";

export function AdminMinistracoes() {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [ministracoes, setMinistracoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importLoading, setImportLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [alert, setAlert] = useState({ open: false, message: "", type: "info" });

  const [formData, setFormData] = useState({
    titulo: "",
    conteudo: "",
    imagem: "",
    autor: "",
    data: "",
    youtubeVideoId: "",
    youtubeUrl: "",
    publicado: true,
  });

  // Extrai o ID do vídeo de qualquer formato de URL/ID do YouTube
  const extractVideoId = (input) => {
    if (!input) return "";
    const patterns = [
      /(?:youtube\.com\/watch\?.*v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/live\/)([a-zA-Z0-9_-]{11})/,
      /^([a-zA-Z0-9_-]{11})$/,
    ];
    for (const pattern of patterns) {
      const match = input.match(pattern);
      if (match) return match[1];
    }
    return "";
  };

  const showAlert = (message, type = "info") => {
    setAlert({ open: true, message, type });
    setTimeout(() => setAlert({ open: false, message: "", type: "info" }), 5000);
  };

  const fetchMinistracoes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl("/api/ministracoes?admin=true"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        setMinistracoes(data.data);
      }
    } catch (error) {
      showAlert("Erro ao carregar ministrações", "error");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchMinistracoes();
  }, [fetchMinistracoes]);

  // Importar do YouTube
  const handleYouTubeImport = async () => {
    try {
      setImportLoading(true);
      const response = await fetch(getApiUrl("/api/ministracoes/youtube-import"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });

      const data = await response.json();
      if (data.success) {
        showAlert(data.message, "success");
        fetchMinistracoes();
      } else {
        showAlert(data.message || "Erro ao importar", "error");
      }
    } catch (error) {
      showAlert("Erro ao conectar com servidor", "error");
    } finally {
      setImportLoading(false);
    }
  };

  // Abrir dialog para criar/editar
  const openDialog = (item = null) => {
    if (item) {
      setEditingItem(item.id);
      setFormData({
        titulo: item.titulo || "",
        conteudo: item.conteudo || "",
        imagem: item.imagem || "",
        autor: item.autor || "",
        data: item.data ? new Date(item.data).toISOString().split("T")[0] : "",
        youtubeVideoId: item.youtubeVideoId || "",
        youtubeUrl: item.youtubeUrl || "",
        publicado: item.publicado !== undefined ? item.publicado : true,
      });
    } else {
      setEditingItem(null);
      setFormData({
        titulo: "",
        conteudo: "",
        imagem: "",
        autor: "",
        data: new Date().toISOString().split("T")[0],
        youtubeVideoId: "",
        youtubeUrl: "",
        publicado: true,
      });
    }
    setDialogOpen(true);
  };

  // Salvar (criar ou atualizar)
  const handleSave = async () => {
    if (!formData.titulo.trim()) {
      showAlert("Título é obrigatório", "error");
      return;
    }

    try {
      const isEditing = editingItem !== null;
      const url = getApiUrl("/api/ministracoes");
      const method = isEditing ? "PUT" : "POST";
      const body = isEditing ? { ...formData, id: editingItem } : formData;

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (data.success) {
        showAlert(data.message, "success");
        setDialogOpen(false);
        fetchMinistracoes();
      } else {
        showAlert(data.message || "Erro ao salvar", "error");
      }
    } catch (error) {
      showAlert("Erro ao conectar com servidor", "error");
    }
  };

  // Alternar publicação
  const togglePublicado = async (item) => {
    try {
      const response = await fetch(getApiUrl("/api/ministracoes"), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ id: item.id, publicado: !item.publicado }),
      });

      const data = await response.json();
      if (data.success) {
        showAlert(
          item.publicado ? "Ministração ocultada" : "Ministração publicada",
          "success"
        );
        fetchMinistracoes();
      }
    } catch (error) {
      showAlert("Erro ao atualizar", "error");
    }
  };

  // Deletar
  const handleDelete = async () => {
    if (!itemToDelete) return;
    try {
      const response = await fetch(getApiUrl("/api/ministracoes"), {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ id: itemToDelete.id }),
      });

      const data = await response.json();
      if (data.success) {
        showAlert("Ministração excluída", "success");
        setDeleteDialogOpen(false);
        setItemToDelete(null);
        fetchMinistracoes();
      } else {
        showAlert(data.message || "Erro ao excluir", "error");
      }
    } catch (error) {
      showAlert("Erro ao conectar com servidor", "error");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("pt-BR", {
      year: "numeric",
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    });
  };

  return (
    <InternalLayout>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate("/dashboard")}
              sx={{ color: "primary.main" }}
            >
              Voltar
            </Button>
            <Typography variant="h5" fontWeight="bold" color="primary.main">
              Ministrações
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              startIcon={importLoading ? <CircularProgress size={20} /> : <YouTubeIcon />}
              onClick={handleYouTubeImport}
              disabled={importLoading}
              sx={{
                borderColor: "#FF0000",
                color: "#FF0000",
                "&:hover": { borderColor: "#CC0000", backgroundColor: "rgba(255,0,0,0.04)" },
              }}
            >
              {importLoading ? "Importando..." : "Importar do YouTube"}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => openDialog()}
              sx={{ backgroundColor: "primary.main", "&:hover": { backgroundColor: "primary.dark" } }}
            >
              Nova Ministração
            </Button>
          </Box>
        </Box>

        {/* Alert */}
        {alert.open && (
          <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert({ ...alert, open: false })}>
            {alert.message}
          </Alert>
        )}

        {/* Loading */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress sx={{ color: "primary.main" }} />
          </Box>
        ) : ministracoes.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Nenhuma ministração cadastrada ainda.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Clique em "Importar do YouTube" para buscar automaticamente as lives das últimas 2 semanas do canal, ou crie uma manualmente.
            </Typography>
          </Paper>
        ) : (
          /* Tabela de ministrações */
          isMobile ? (
            // Mobile: Cards
            <Grid container spacing={2}>
              {ministracoes.map((item) => (
                <Grid item xs={12} key={item.id} sx={{ width: "100%" }}>
                  <Card sx={{ 
                    display: "flex", 
                    width: "100%", 
                    maxWidth: "100%", 
                    height: 100,
                    opacity: item.publicado ? 1 : 0.6, 
                    overflow: "hidden",
                    boxSizing: "border-box"
                  }}>
                    {item.imagem && (
                      <CardMedia
                        component="img"
                        sx={{ width: 100, height: 100, flexShrink: 0, objectFit: "cover" }}
                        image={item.imagem}
                        alt={item.titulo}
                      />
                    )}
                    <Box sx={{ 
                      flex: 1, 
                      width: `calc(100% - ${item.imagem ? '100px' : '0px'})`,
                      minWidth: 0, 
                      maxWidth: `calc(100% - ${item.imagem ? '100px' : '0px'})`,
                      p: 1.5, 
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between"
                    }}>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold" noWrap>
                          {item.titulo}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap component="div">
                          {formatDate(item.data)} {item.autor && `• ${item.autor}`}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", flexWrap: "wrap", overflow: "hidden" }}>
                        <Chip
                          size="small"
                          label={item.publicado ? "Publicado" : "Rascunho"}
                          color={item.publicado ? "success" : "default"}
                          sx={{ fontSize: "0.65rem", height: 22 }}
                        />
                        {item.youtubeVideoId && (
                          <Chip size="small" icon={<YouTubeIcon sx={{ fontSize: 14 }} />} label="YT" sx={{ fontSize: "0.65rem", height: 22 }} />
                        )}
                        <IconButton size="small" onClick={() => openDialog(item)}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" onClick={() => togglePublicado(item)}>
                          {item.publicado ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => { setItemToDelete(item); setDeleteDialogOpen(true); }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            // Desktop: Table
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "background.default" }}>
                    <TableCell>Thumb</TableCell>
                    <TableCell>Título</TableCell>
                    <TableCell>Autor</TableCell>
                    <TableCell>Data</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>YouTube</TableCell>
                    <TableCell align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ministracoes.map((item) => (
                    <TableRow key={item.id} sx={{ opacity: item.publicado ? 1 : 0.6 }}>
                      <TableCell>
                        {item.imagem ? (
                          <Box
                            component="img"
                            src={item.imagem}
                            alt=""
                            sx={{ width: 80, height: 45, objectFit: "cover", borderRadius: 1 }}
                          />
                        ) : (
                          <Box sx={{ width: 80, height: 45, backgroundColor: "background.default", borderRadius: 1 }} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold" sx={{ maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {item.titulo}
                        </Typography>
                      </TableCell>
                      <TableCell>{item.autor || "-"}</TableCell>
                      <TableCell>{formatDate(item.data)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={item.publicado ? "Publicado" : "Rascunho"}
                          color={item.publicado ? "success" : "default"}
                        />
                      </TableCell>
                      <TableCell>
                        {item.youtubeVideoId ? (
                          <Chip size="small" icon={<YouTubeIcon />} label="Sim" color="error" variant="outlined" />
                        ) : "-"}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton onClick={() => openDialog(item)} title="Editar"><EditIcon /></IconButton>
                        <IconButton onClick={() => togglePublicado(item)} title={item.publicado ? "Ocultar" : "Publicar"}>
                          {item.publicado ? <VisibilityIcon color="success" /> : <VisibilityOffIcon />}
                        </IconButton>
                        <IconButton color="error" onClick={() => { setItemToDelete(item); setDeleteDialogOpen(true); }} title="Excluir">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        )}

        {/* Dialog de Criar/Editar */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
          <DialogTitle sx={{ backgroundColor: "primary.main", color: "white" }}>
            {editingItem ? "Editar Ministração" : "Nova Ministração"}
          </DialogTitle>
          <DialogContent sx={{ pt: "20px !important" }}>
            <TextField
              fullWidth label="Título" value={formData.titulo}
              onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
              sx={{ mb: 2 }} required
            />
            <TextField
              fullWidth label="Autor / Preletor" value={formData.autor}
              onChange={(e) => setFormData({ ...formData, autor: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth label="Data" type="date" value={formData.data}
              onChange={(e) => setFormData({ ...formData, data: e.target.value })}
              sx={{ mb: 2 }} InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth label="Descrição" value={formData.conteudo}
              onChange={(e) => setFormData({ ...formData, conteudo: e.target.value })}
              multiline rows={5} sx={{ mb: 2 }}
            />
            <TextField
              fullWidth label="URL da Imagem (thumbnail)" value={formData.imagem}
              onChange={(e) => setFormData({ ...formData, imagem: e.target.value })}
              sx={{ mb: 2 }} helperText="Deixe vazio para usar thumbnail do YouTube"
            />
            {formData.imagem && (
              <Box sx={{ mb: 2, textAlign: "center" }}>
                <Box
                  component="img"
                  src={formData.imagem}
                  alt="Preview"
                  sx={{ maxWidth: "100%", maxHeight: 200, objectFit: "cover", borderRadius: 1 }}
                />
              </Box>
            )}
            <TextField
              fullWidth label="URL ou ID do Vídeo no YouTube"
              value={formData.youtubeUrl || formData.youtubeVideoId}
              onChange={(e) => {
                const input = e.target.value.trim();
                const videoId = extractVideoId(input);
                setFormData({
                  ...formData,
                  youtubeVideoId: videoId,
                  youtubeUrl: videoId ? `https://www.youtube.com/watch?v=${videoId}` : input,
                  imagem: videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : formData.imagem,
                });
              }}
              sx={{ mb: 2 }}
              helperText="Cole a URL completa do YouTube ou apenas o ID do vídeo (ex: dQw4w9WgXcQ)"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.publicado}
                  onChange={(e) => setFormData({ ...formData, publicado: e.target.checked })}
                  color="success"
                />
              }
              label="Publicado (visível no site)"
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button
              variant="contained"
              onClick={handleSave}
              sx={{ backgroundColor: "primary.main", "&:hover": { backgroundColor: "primary.dark" } }}
            >
              {editingItem ? "Salvar Alterações" : "Criar Ministração"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog de Confirmação de Exclusão */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Confirmar Exclusão</DialogTitle>
          <DialogContent>
            <Typography>
              Tem certeza que deseja excluir a ministração "{itemToDelete?.titulo}"?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
            <Button variant="contained" color="error" onClick={handleDelete}>
              Excluir
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </InternalLayout>
  );
}
